import { Icon } from '@/comp/icon/icon';

import './tooltip.scss';
import { PreloadedIconKey } from '@/_config/icons';
import { ReactNode, useCallback, useId, useRef, useState } from 'react';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';
type ResolvedPlacement = Exclude<TooltipPlacement, 'auto'>;

interface TooltipProps {
    children: ReactNode;
    icon?: PreloadedIconKey;
    iconSize?: number;
    classList?: string;
    placement?: TooltipPlacement;
}

export const Tooltip = ({ icon, iconSize = 20, placement = 'auto', classList = '', children }: TooltipProps) => {
    const [visible, setVisible] = useState(false);
    const [resolved, setResolved] = useState<ResolvedPlacement>(placement === 'auto' ? 'bottom' : placement);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const id = useId();

    const resolveAndShow = useCallback(() => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setResolved(placement === 'auto' ? resolveAuto(rect) : placement);
        }
        setVisible(true);
    }, [placement]);

    function resolveAuto(rect: DOMRect): ResolvedPlacement {
        const space = {
            top: rect.top,
            bottom: window.innerHeight - rect.bottom,
            left: rect.left,
            right: window.innerWidth - rect.right,
        };
        return (Object.keys(space) as ResolvedPlacement[]).reduce((a, b) => (space[a] >= space[b] ? a : b));
    }

    return (
        <div
            ref={wrapperRef}
            className={`tooltip-container ${classList}`}
            onMouseEnter={resolveAndShow}
            onMouseLeave={() => setVisible(false)}
            onFocus={resolveAndShow}
            aria-describedby={`tooltip-info-${id}`}
            onBlur={() => setVisible(false)}>
            <Icon size={iconSize} name={icon ? icon : 'infoFilled'} className="color-grey" ariaLabel="info" />
            <span id={`tooltip-info-${id}`} className={`tooltip ${resolved}`} role="tooltip" aria-hidden={!visible}>
                {children}
            </span>
        </div>
    );
};
